import { useForm } from "react-hook-form";
import { useState, useRef, useEffect } from "react";
import { requestPasswordResetAPI } from "../services/authService";

import logo from "../assets/images/logo.png";
import logo_2 from "../assets/images/logo-3.png";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Background1 from "../assets/images/Slider 1.jpg";
import Background2 from "../assets/images/Slider 2.jpg";
import Background3 from "../assets/images/Slider 3.jpg";
import Background4 from "../assets/images/Slider 4.jpg";
import { toast } from "react-hot-toast";

function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const backgrounds = [Background1, Background2, Background3, Background4];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgrounds.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const res = await requestPasswordResetAPI(data.email);
      if (res.data.status === "success") {
        console.log("Data:", res.data);
        toast.success("Reset link sent to your email.");
        reset({ email: "" });
      } else {
        toast.error(res.data.message || "Something went wrong. Try again.");
      }
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data) {
        if (err.response.data.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong. Try again.");
        }
      }
      else if (err.message === "Network Error") {
        toast.error("Network error. Please check your connection.");
      }
      else {
        toast.error("Something went wrong. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full z-0">
        {backgrounds.map((bg, index) => (
          <img
            key={index}
            src={bg}
            alt={`Background ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentBgIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      <div className="relative flex flex-col gap-8 z-10 flex items-center justify-center w-full h-full px-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="relative w-full max-w-md rounded-2xl bg-white/70 shadow-xl p-10 space-y-6 text-center z-10"
          noValidate // Prevents browser default validation
        >
          <img src={logo} alt="Logo" className="mx-auto w-50 pb-2" />

          <h2 className="text-gray-800 text-xl font-semibold">
            Forgot Your Password?
          </h2>
          <p className="text-gray-800 text-sm">
            Enter your email to receive a password reset link.
          </p>

          <div className="space-y-4">
            {/* Email */}
            <div className="relative">
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                type="email"
                placeholder="Email"
                className={`w-full px-4 py-3 rounded-full text-sm text-black placeholder:text-black/60 bg-white/90 border border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#27ade3] transition-all ${
                  errors.email ? "border-red-500 focus:ring-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 text-left ml-4">
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 rounded-full bg-gradient-to-r from-[#1a325d] to-[#27ade3] 
             text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 
             border border-[#27ade3] hover:border-white/50
             hover:scale-[1.02] active:scale-95
             relative overflow-hidden group ${
               isLoading ? "opacity-80 cursor-not-allowed" : ""
             }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending...
              </div>
            ) : (
              <>
                <span className="relative z-10">Send Reset Link</span>
                <span className="absolute inset-0 bg-[#27ade3]/30 opacity-0 group-hover:opacity-100 transition duration-300 blur-sm"></span>
              </>
            )}
          </button>

          <div className="mt-2">
            <Link
              to="/login"
              className="text-lg text-[#3498db] font-medium transition duration-300 hover:text-black/70 hover:underline flex items-center justify-center"
            >
              <ArrowLeft className="mr-1" />
              Back To Login
            </Link>
          </div>
        </form>

        <img src={logo_2} alt="Logo" className="mx-auto w-50 pb-2" />
      </div>
    </div>
  );
}

export default ForgotPassword;
